/* tslint:disable */
import { EventEmitter, Inject, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { UserSettingsService } from './user-settings.service';
import { TranslateService } from '@ngx-translate/core';
import { InactiveOwner } from '../models/users.model';
import { catchError, finalize, map, retryWhen, scan } from 'rxjs/operators';
import { SpinnerService } from '../common/spinner/spinner.service';
import { Facility, StatusReasons } from '../models/masterdata.model';
import {
  Amenity,
  AttachmentFileExtension,
  AttachmentFileType,
  Building,
  Configuration,
  EventType,
  Item,
  ItemWithDescription,
  ItemWithDisplayName,
  JobTitle,
  Neighborhood,
  ServiceOrderType,
  SpaceFeature,
  SpaceSetupType,
  Status,
  TaskType,
  TimeZone,
  ViewTab,
  VirtualBookingAdditionalDetail,
  SpaceType,
  ItemForSubType,
  Technology,
  CateringOrder,
} from '../models/masterdata.model';
import { DomSanitizer } from '@angular/platform-browser';
import * as moment from 'moment';
import 'moment-timezone';
import { PreferenceDetails } from '../models/preferences.model';
import { ToasterService, ToasterType } from '../common/toaster/toaster.service';
import { TimeZoneMatchInput, TimeZoneMatchResponse } from '../models/common.model';
import { APP_CONFIG, AppConfiguration } from '../configs/app-configuration';

@Injectable()
export class DataContextService {
  public static BASE_URL: string;

  public static ROWS_PER_PAGE: number;
  public static RECOMMENDATION_LIST_PAGE_SIZE: number;
  public static LoggedInUserId: number;
  public masterDadaLoaded$ = new BehaviorSubject<boolean>(false);
  public Breadcrumb: string;
  public BreadcrumbNavigation: string;
  public static FacilityArray: Array<Facility>;
  public static TimeZones: TimeZone[];
  public static VirtualBookingTechnologyAccounts: Item[] = [];
  public static VirtualBookingAdditionalDetails: VirtualBookingAdditionalDetail[] = [];
  public static TaskTypes: TaskType[];
  public static ServiceOrderTypes: ServiceOrderType[];
  public static Neighborhoods: Neighborhood[] = [];
  public static Series: Item[];
  public static TaskStatus: Status[];
  public static CateringStatus: Status[];
  public static WaitlistStatus: Status[] = [];
  public static BookingStatus: Status[];
  public static ReservationStatus: Status[];
  public static VirtualBookingStatus: Status[] = [];
  public static SpaceFeatures: SpaceFeature[];
  public static EventTypes: EventType[];
  public static SpaceSetupTypes: SpaceSetupType[];
  public static Configurations: Configuration[];
  public static MemberFirmConfigurations: Configuration[] = [];
  public static ViewTabs: ViewTab[];
  public static MeetingWaitlistRequestTypes: ItemWithDisplayName[];
  public static MeetingWaitlistStatus: Status[];
  public static AttachmentFileExtensions: AttachmentFileExtension[] = [];
  public static AttachmentFileTypes: AttachmentFileType[] = [];
  public static StatusReasons: StatusReasons[];
  public static SpaceSubTypes: ItemWithDescription[];
  public static Amenities: Amenity[];
  public static JobTitles: JobTitle[];
  public static MealOptions: Item[];
  public static Buildings: Building[] = [];
  public static FilterSelectedCountryBuildings: Building[] = [];
  public static SpaceTypes: SpaceType[];

  public static UserData: any;
  public static ServiceOrderFilter_arr: Array<ItemForSubType>;

  private static RequestCounter: number = 0;
  public static VirtualBookingTechnologyTypes: Array<Technology> = [];
  public static CateringOrder_Type: Array<CateringOrder>;
  public preferenceRefresh$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public static readonly MAX_AVATAR_NUMBER = 20;
  private static userNotificationDataChange: EventEmitter<any> = new EventEmitter();

  constructor(
    private _http: HttpClient,
    private spinner: SpinnerService,
    private userSettingsService: UserSettingsService,
    private injector: Injector,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    private toasterService: ToasterService,
    @Inject(APP_CONFIG) private appConfiguration: AppConfiguration,
  ) {
    DataContextService.BASE_URL =
    this.userSettingsService.appSettings.HotelingCloudApiUrl + this.userSettingsService.appSettings.HotelingCloudApiVersion + '/';
    translate.setDefaultLang(this._defaultLanguage);
    translate.use(this._defaultLanguage);
  }

  translateText(input: string): string {
    return this.translate.instant(input);
  }

  private _defaultLanguage: string = 'en-US';
  private _defaultLanguageCode: string;

  getData<T>(
    _dataUrl: string,
    showGlobalSpinner: boolean = true,
    isDelegate: boolean = false,
    continueOnError: boolean = false
  ): Observable<APIResponse> {
    if (DataContextService.RequestCounter == 0 && showGlobalSpinner) {
      this.spinner.start();
    }
    const headerDict = {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    };
    _dataUrl = `${DataContextService.BASE_URL}${_dataUrl}`;
    let headers = new HttpHeaders(headerDict);
    headers = this.setoAuthHeaders(headers, isDelegate);
    //increment request counter by 1
    if (showGlobalSpinner) {
      DataContextService.RequestCounter = DataContextService.RequestCounter + 1;
    }

    return this._http
      .get(_dataUrl, {
        headers: headers,
        observe: 'response',
      })
      .pipe(
        retryWhen((error) =>
          error.pipe(
            scan((acc, error) => {
              if (acc > 1 && error.status == 401) {
                throw error;
              } else if (error.status != 401) {
                throw error;
              }
              return error.status == 401 ? acc + 1 : acc;
            }, 1)
          )
        ),
        map((response: any) => {
          const result = {
            status: response.status,
            statusText: response.statusText,
            data: response['body'],
          };
          return result;
        }),
        catchError((error: HttpErrorResponse) => this.handleError(error, continueOnError)),
        finalize(() => {
          if (showGlobalSpinner) {
            DataContextService.RequestCounter = DataContextService.RequestCounter - 1;
            if (DataContextService.RequestCounter == 0) {
              this.spinner.stop();
            }
          }
        })
      );
  }

  getImage(url: any, continueOnError: boolean = false) {
    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'text/plain');
    headers = headers.append('Content-Transfer-Encoding', 'base64');
    headers = this.setoAuthHeaders(headers, false);
    if (DataContextService.RequestCounter == 0) {
      this.spinner.start();
      DataContextService.RequestCounter = DataContextService.RequestCounter + 1;
    }
    return this._http
      .get(url, {
        headers: headers,
        responseType: 'arraybuffer',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => this.handleError(error, continueOnError)),
        finalize(() => {
          DataContextService.RequestCounter = DataContextService.RequestCounter - 1;
          if (DataContextService.RequestCounter == 0) {
            this.spinner.stop();
          }
        })
      );
  }

  arrayBufferToBase64(buffer) {
    let binary = '';
    let bytes = new Uint8Array(buffer);
    let len = bytes.byteLength;
    for (var i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  setDataToImageBody(data, isIcon = false) {
    if (isIcon) {
      return this.sanitizer.bypassSecurityTrustUrl('data:image/svg+xml;base64,' + data);
    }
    return this.sanitizer.bypassSecurityTrustUrl('data:image/png;base64,' + data);
  }

  isCacheExpired(lastUpdated: string | Date, expirationTimeKey: string): boolean {
    let expirationTime = this.userSettingsService.appSettings[expirationTimeKey].split(':');
    let currentDate = moment().format('YYYY-MM-DDTHH:mm:ss');
    if (
      moment(lastUpdated)
        .add(Number(expirationTime[0]) * 60 + Number(expirationTime[1]), 'minutes')
        .isBefore(currentDate)
    ) {
      return true;
    } else {
      return false;
    }
  }

  postData(
    _dataUrl: string,
    data: Object,
    showGlobalSpinner: boolean = true,
    stopSpinner?: boolean,
    isDelegate: boolean = false
  ): Observable<APIResponse> {
    if (DataContextService.RequestCounter == 0 && showGlobalSpinner) {
      this.spinner.start();
    }
    let bodyString = JSON.stringify(data);

    if (showGlobalSpinner) {
      DataContextService.RequestCounter = DataContextService.RequestCounter + 1;
    }
    _dataUrl = `${DataContextService.BASE_URL}${_dataUrl}`;
    let headers = new HttpHeaders();
    headers = this.setHeaderTypeAsJson(headers, isDelegate);
    return this._http.post(_dataUrl, bodyString, { headers: headers, observe: 'response' }).pipe(
      retryWhen((error) =>
        error.pipe(
          scan((acc, error) => {
            if (acc > 1 && error.status == 401) {
              throw error;
            } else if (error.status != 401) {
              throw error;
            }
            return error.status == 401 ? acc + 1 : acc;
          }, 1)
        )
      ),
      map((response: any) => {
        const result = {
          status: response.status,
          statusText: response.statusText,
          data: response['body'],
        };
        return result;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        if (showGlobalSpinner) {
          DataContextService.RequestCounter = DataContextService.RequestCounter - 1;
          if (DataContextService.RequestCounter == 0) {
            !stopSpinner && this.spinner.stop();
          }
        }
      })
    );
  }

  patchData(_dataUrl: string, data: Object, showGlobalSpinner: boolean = true, isDelegate: boolean = false): Observable<APIResponse> {
    if (DataContextService.RequestCounter == 0 && showGlobalSpinner) {
      this.spinner.start();
    }
    let bodyString = JSON.stringify(data);

    if (showGlobalSpinner) {
      DataContextService.RequestCounter = DataContextService.RequestCounter + 1;
    }
    _dataUrl = `${DataContextService.BASE_URL}${_dataUrl}`;
    let headers = new HttpHeaders();
    headers = this.setHeaderTypeAsJson(headers, isDelegate);
    return this._http.patch(_dataUrl, bodyString, { headers: headers, observe: 'response' }).pipe(
      retryWhen((error) =>
        error.pipe(
          scan((acc, error) => {
            if (acc > 1 && error.status == 401) {
              throw error;
            } else if (error.status != 401) {
              throw error;
            }
            return error.status == 401 ? acc + 1 : acc;
          }, 1)
        )
      ),
      map((response: any) => {
        const result = {
          status: response.status,
          statusText: response.statusText,
          data: response['body'],
        };
        return result;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        if (showGlobalSpinner) {
          DataContextService.RequestCounter = DataContextService.RequestCounter - 1;
          if (DataContextService.RequestCounter == 0) {
            this.spinner.stop();
          }
        }
      })
    );
  }

  deleteData(_dataUrl: string, data?: any, showGlobalSpinner: boolean = true, isDelegate: boolean = false): Observable<APIResponse> {
    if (DataContextService.RequestCounter == 0 && showGlobalSpinner) {
      this.spinner.start();
    }
    if (showGlobalSpinner) {
      DataContextService.RequestCounter = DataContextService.RequestCounter + 1;
    }
    _dataUrl = `${DataContextService.BASE_URL}${_dataUrl}`;
    let headers = new HttpHeaders();
    headers = this.setHeaderTypeAsJson(headers, isDelegate);

    return this._http.request('delete', _dataUrl, { body: JSON.stringify(data), headers: headers, observe: 'response' }).pipe(
      retryWhen((error) =>
        error.pipe(
          scan((acc, error) => {
            if (acc > 1 && error.status == 401) {
              throw error;
            } else if (error.status != 401) {
              throw error;
            }
            return error.status == 401 ? acc + 1 : acc;
          }, 1)
        )
      ),
      map((response: any) => {
        const result = {
          status: response.status,
          statusText: response.statusText,
          data: response['body'],
        };
        return result;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        if (showGlobalSpinner) {
          DataContextService.RequestCounter = DataContextService.RequestCounter - 1;
          if (DataContextService.RequestCounter == 0) {
            this.spinner.stop();
          }
        }
      })
    );
  }

  setHeaderTypeAsJson(headers: HttpHeaders, isDelegate: boolean): HttpHeaders {
    headers = headers.append('content-type', 'application/json');
    headers = this.setoAuthHeaders(headers, isDelegate);
    return headers;
  }

  putData(
    _dataUrl: string,
    data: Object,
    setHeader?: boolean,
    showGlobalSpinner: boolean = true,
    isDelegate: boolean = false
  ): Observable<APIResponse> {
    if (DataContextService.RequestCounter == 0 && showGlobalSpinner) {
      this.spinner.start();
    }
    let bodyString = JSON.stringify(data);

    if (showGlobalSpinner) {
      DataContextService.RequestCounter = DataContextService.RequestCounter + 1;
    }
    _dataUrl = `${DataContextService.BASE_URL}${_dataUrl}`;
    let headers = new HttpHeaders();
    headers = this.setHeaderTypeAsJson(headers, isDelegate);
    return this._http.put(_dataUrl, bodyString, { headers: headers, observe: 'response' }).pipe(
      retryWhen((error) =>
        error.pipe(
          scan((acc, error) => {
            if (acc > 1 && error.status == 401) {
              throw error;
            } else if (error.status != 401) {
              throw error;
            }
            return error.status == 401 ? acc + 1 : acc;
          }, 1)
        )
      ),
      map((response: any) => {
        const result = {
          status: response.status,
          statusText: response.statusText,
          data: response['body'],
        };
        return result;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        if (showGlobalSpinner) {
          DataContextService.RequestCounter = DataContextService.RequestCounter - 1;
          if (DataContextService.RequestCounter == 0) {
            this.spinner.stop();
          }
        }
      })
    );
  }

  setoAuthHeaders(headers: HttpHeaders, isDelegate: boolean): HttpHeaders {
    headers = headers.append('Authorization', this.appConfiguration.tokenPrefix + ' ' + window['hotelingDataToken']);
    headers = headers.append('X-Tenant-Id', this.appConfiguration.abbreviation);
    headers = headers.append('X-Timestamp', new Date().toUTCString());
    headers = headers.append('accept', 'application/json');
    headers = this.setAcceptLanguageHeaders(headers);
    headers = headers.append('X-UseSpace', isDelegate ? XUseSpaceEnum[XUseSpaceEnum.Delegate] : XUseSpaceEnum[XUseSpaceEnum.User]);
    return headers;
  }

  setAcceptLanguageHeaders(headers: HttpHeaders): HttpHeaders {
    headers = headers.append('accept-language', this.defaultLanguage);
    return headers;
  }

  private handleError(error: HttpErrorResponse, continueOnError: boolean = false): Observable<APIResponse> {
    let result = { status: error.status, statusText: error.statusText, data: error['error'] ? error['error'] : [] };
    if (error.status == 400) {
      result.data.message = result.data.modelErrors ? result.data.modelErrors[0].error : result.data.message;
    }
    if (continueOnError == true) {
      result.data = [];
      return new Observable((ob) => {
        ob.next(result);
        ob.complete();
      });
    } else {
      return new Observable((ob) => {
        ob.next(result);
        ob.complete();
      });
    }
  }

  get defaultLanguage() {
    return this._defaultLanguage;
  }

  set defaultLanguage(language: string) {
    this._defaultLanguage = language;
  }
  get defaultLanguageCode() {
    return this._defaultLanguageCode;
  }
  set defaultLanguageCode(langCode: string) {
    this._defaultLanguageCode = langCode;
  }

  fetchFromLocalStorage(key: string) {
    return localStorage.getItem(key);
  }

  storeInLocalStorage(key: string, data: any) {
    localStorage.setItem(key, JSON.stringify(data));
  }
  removeFromLocalStorage(key: string) {
    localStorage.removeItem(key);
  }
  get languageId(): number {
    const langId = this.fetchFromSessionStorage('DefaultLanguageId');
    if (!langId) {
      return 1;
    }
    return Number(langId);
  }
  set languageId(id: number) {
    this.storeInSessionStorage('DefaultLanguageId', id);
  }
  get languageCode() {
    return this.fetchFromSessionStorage('DefaultLanguageCode')
  }
  set languageCode(code: string) {
    this.storeInSessionStorage('DefaultLanguageCode', code)
  }
  removeInactiveBuildingFloors(viewData: PreferenceDetails): PreferenceDetails {
    let activeBuildings: number[] = [];
    let activeFloors:number[] = [];
    const buildings = viewData.buildings.split(',');
    const floors = viewData.floors.split(',');
    if (buildings.length) {
      buildings.forEach(building => {
        const activeBuilding = DataContextService.Buildings.find(i => i.id === Number(building));
        if (activeBuilding) {
          let buildingFloors: number[] = [];
          if (floors[0] === 'All')
            buildingFloors = activeBuilding.floors.map(i => i.uniqueKey);
          else {
            floors.forEach(floor => {
              const activeFloor = activeBuilding.floors.find(i => i.uniqueKey === Number(floor))
              if (activeFloor)
                buildingFloors = [...buildingFloors, activeFloor.uniqueKey];
            });
            if (buildingFloors.length) {
              activeBuildings = [...activeBuildings, activeBuilding.id];
              activeFloors = [...activeFloors, ...buildingFloors];
            }
          }
        }
      });
    }
    viewData.buildings = activeBuildings.length ? activeBuildings.join(',') : null;
    viewData.floors = activeFloors.length ? activeFloors.join(',') : null;
    return viewData;
  }

  showErrorToaster(res: any, isServiceCall: boolean = true) {
    this.toasterService.show({ message: res.data.message, type: ToasterType.error });
    return null;
  }
  setDefaultLanguage(key: string) {
    this.translate.setDefaultLang(key);
    this._defaultLanguage = key;
    this.translate.use(this._defaultLanguage);
    this.storeInSessionStorage('DefaultLanguage', key);
  }

  fetchFromSessionStorage(key: string) {
    return sessionStorage.getItem(key);
  }

  storeInSessionStorage(key: string, data: any) {
    sessionStorage.setItem(key, data);
  }

  //status check functions
  checkDefiniteStatus(statusName: string): boolean {
    return statusName == 'Definite';
  }

  checkConfirmedStatus(statusName: string): boolean {
    return statusName == 'Confirmed';
  }

  checkCancelledStatus(statusName: string): boolean {
    return statusName == 'Cancelled';
  }

  checkExpiredStatus(statusName: string): boolean {
    return statusName == 'Expired';
  }

  checkRequestedStatus(statusName: string): boolean {
    return statusName == 'Requested';
  }

  checkReservedStatus(statusName: string): boolean {
    return statusName == 'Reserved';
  }

  checkCheckedInStatus(statusName: string): boolean {
    return statusName == 'CheckedIn';
  }

  checkReleasedStatus(statusName: string): boolean {
    return statusName == 'Released';
  }

  checkDidNotMaterializeStatus(statusName: string): boolean {
    return statusName == 'DidNotMaterialize';
  }

  checkUnfulfilledStatus(status: string): boolean {
    return status === 'Unfulfilled';
  }

  checkEmptyStatus(statusName: string): boolean {
    return statusName == 'Empty';
  }

  checkReviewedStatus(statusName: string): boolean {
    return statusName == 'Review';
  }

  checkPendingStatus(statusName: string): boolean {
    return statusName == 'Pending';
  }

  checkCompletedStatus(statusName: string): boolean {
    return statusName == 'Completed';
  }

  //spaceType check functions
  checkPrivateSpace(spaceType) {
    return spaceType == 'PrivateSpace';
  }

  checkWorkstation(spaceType) {
    return spaceType == 'Workstation';
  }

  checkMeetingRoom(spaceType) {
    return spaceType == 'MeetingRoom';
  }

  checkOpenSeating(spaceType) {
    return spaceType == 'OpenSeating';
  }
  checkforOwnerName(owner: InactiveOwner): boolean {
    if (owner.FirstName == null && owner.LastName == '') return false;
    else if (owner.FirstName == null && owner.LastName == null) return false;
    else if (owner.FirstName == '' && owner.LastName == '') return false;
    else if (owner.FirstName == '' && owner.LastName == null) return false;
    else return true;
  }
  checkforFirstNameorLastName(owner: InactiveOwner) {
    if (owner.FirstName != null && owner.LastName != null) {
      return owner.FirstName + ' ' + owner.LastName;
    } else if (owner.FirstName != null && owner.LastName == null) {
      return owner.FirstName;
    } else if (owner.FirstName == null && owner.LastName != null) {
      return owner.LastName;
    } else return '';
  }

  getUserFirstName(user): string {
    if (user.firstName) return user.firstName;
    else if (!user.firstName && !user.lastName) return user.userName;
    else return '';
  }
  static userNotificationDataEmitter(event) {
    this.userNotificationDataChange.emit(event);
  }

  static getUserNotificationDataEvent() {
    return this.userNotificationDataChange;
  }

  removeNullValuesFromObject(input) {
    Object.keys(input).forEach((key) => {
      if (typeof input[key] === 'object') {
        if (input[key]) {
          Object.keys(input[key]).forEach((subKey) => {
            if (input[key][subKey] == null || input[key][subKey].length == 0) delete input[key][subKey];
          });
          if (Object.keys(input[key]).length == 0) delete input[key];
        } else delete input[key];
      } else {
        if (input[key] == null) delete input[key];
      }
    })
    return input;
  }
  getTimeZoneMatchData(input: TimeZoneMatchInput): TimeZoneMatchResponse[] {
    const timeZoneMatchData = input.timeZones.filter((item, index) => input.timeZones.indexOf(item) === index).map(timezone => {
      const data: TimeZoneMatchResponse = {
        startDate: new Date(moment.parseZone(input.startDate).tz(timezone).format('l LT')),
        endDate: new Date(moment.parseZone(input.endDate).tz(timezone).format('l LT')),
        timeZone: timezone
      }
      return data;
    });
    return timeZoneMatchData;
  }

  getStatusReasons(statusName: string) {
    const statusToStatusReasonMap = {
      'Cancelled': 5,
      'DidNotMaterialize': 1000
    };

    return DataContextService.StatusReasons.find(statusReason => statusReason.id == statusToStatusReasonMap[statusName]).statusReasons;
  };
}

export enum XUseSpaceEnum {
  Delegate,
  User,
}

export interface APIResponse {
  status: number;
  statusText: string;
  data: any;
}
